import React, {forwardRef, memo, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useMergeRefs} from '@chakra-ui/react'
import {ButtonGroup as ChakraButtonGroup} from '@chakra-ui/react3'
import {useNonce} from '../../../../hooks'

const IS_TEST = process.env.NODE_ENV === 'test'

// DO NOT USE MODERN JS INSIDE OR REFERENCE ANYTHING OUTSIDE OF THIS FUNCTION
// This function is stringified and embedded in a script tag for SSR.
// Modern JS features may not be supported in all browsers, and external references
// will be undefined in the inline script context.
function preHydrationWrapDetector() {
    const c = document.currentScript.previousElementSibling
    const b = [...c.children]
        // chakra during ssr renders the styles in a style tag as sibling to the component
        // we need to filter them out to avoid counting them as visible children
        .filter((child) => child.tagName.toUpperCase() !== 'STYLE')
    if (b.length < 2) return

    const f = () => {
        c.dataset.wrapped = b[0].offsetTop !== b[b.length - 1].offsetTop
    }
    const observer = new ResizeObserver(f)
    observer.observe(c)
    f()

    // store observer for cleanup
    c._wrapObserver = observer
}

/**
 * ButtonGroup component to layout buttons horizontally with wrapping support on mobile.
 *
 * On desktop, buttons are always displayed side by side.
 * On mobile, if buttons cannot fit side by side, they are vertically stacked and expand to 100% of the container width available.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - The button elements to be grouped.
 * @param {React.Ref} ref - Ref to the container element.
 * @returns {JSX.Element} Rendered ButtonGroup element.
 */
const ButtonGroup = memo(
    forwardRef(function ButtonGroup(props, ref) {
        const nonce = useNonce()

        const containerRef = useRef(null)
        const mergedRef = useMergeRefs(ref, containerRef)

        // wrap detection using ResizeObserver to add data-wrapped attribute to container
        useEffect(() => {
            const container = containerRef.current
            if (!container) return

            // destroy the observer from the ssr embedded script if it exists, to use the one created here
            if (container._wrapObserver) {
                container._wrapObserver.disconnect()
                delete container._wrapObserver
            }

            const containerChildren = container.children
            if (containerChildren.length < 2) return

            let lastWrapped = false
            const checkForWrapping = () => {
                const wrapped =
                    containerChildren[0].offsetTop !==
                    containerChildren[containerChildren.length - 1].offsetTop
                if (wrapped !== lastWrapped) {
                    container.dataset.wrapped = wrapped
                    lastWrapped = wrapped
                }
            }

            const observer = new ResizeObserver(checkForWrapping)
            observer.observe(container)
            checkForWrapping()

            return () => {
                observer.disconnect()
            }
        }, [])

        return (
            <>
                <ChakraButtonGroup
                    {...props}
                    ref={mergedRef}
                    orientation="horizontal"
                    css={{
                        display: 'flex',
                        flexWrap: {base: 'wrap', s: 'nowrap'}, // wrap only on mobile

                        // inline style - if needed to handle different variants could be extracted to recipe
                        gap: 3,
                        alignItems: 'flex-start',

                        // default children size
                        '& > *': {
                            flex: '0 1 auto' // size to content
                        },

                        // wrap detected - make buttons full width
                        '&[data-wrapped=true] > *': {
                            flex: '1 1 max-content'
                        }
                    }}
                />
                {
                    // DO NOT INCLUDE THIS IN TESTS as it crashes istanbul coverage
                    // with "ReferenceError: cov_233hcnig72 is not defined" because we
                    // dynamically create a new function in the script tag
                    !IS_TEST && (
                        // inline script to detect wrapping of buttons as soon as the component is rendered
                        // only used for ssr until hydration
                        <script
                            lang="javascript"
                            type="text/javascript"
                            nonce={nonce}
                            dangerouslySetInnerHTML={{
                                __html: `(${preHydrationWrapDetector.toString()})()`
                            }}
                        />
                    )
                }
            </>
        )
    })
)

if (process.env.NODE_ENV !== 'production') {
    ButtonGroup.displayName = 'ButtonGroup'
}

ButtonGroup.propTypes = {
    children: PropTypes.node
}

export default ButtonGroup
